/** Inline edit-on-click field widget family.
 *
 * Renders a value as compact, clickable text (read mode); clicking swaps in the
 * type-appropriate input (edit mode). Edits commit live to the parent on each
 * keystroke — so a separate Save button never races a still-open field — while
 * Escape reverts to the value captured when editing began. A `pending` flag
 * (value differs from what's persisted) renders the read-mode value distinctly.
 *
 * One component covers the family (int / text / email / bool / select / list):
 * - bool → a clickable chip that toggles in place (no separate input);
 * - list → chips you can delete + an add box while editing;
 * - select → read text ⇄ dropdown, with a chevron so a constrained field looks
 *   constrained rather than identical to free text;
 * - int / short text → inline standard-underline input that auto-sizes to content
 *   (aligned with the read text, so toggling doesn't shift the row);
 * - long text / email → a Popover with a wide, wrapping input, so length isn't
 *   constrained by the row and the read text stays put as the anchor.
 *
 * `FieldBlock` is the chrome around any control — the label row (label, required
 * marker, help popover) and the footer (why a required value is still missing, a
 * validation message, the platform default and a one-click revert). It is
 * exported separately so a caller supplying its own richer control still gets
 * the same anatomy; see the preflight field editor's widget dispatch.
 *
 * Value is controlled by the parent. Persistence is the parent's job; the backend
 * is always the authoritative check.
 */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  IconButton,
  Link,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useRef, useState, type MouseEvent, type ReactNode } from 'react';

import { FieldHelp } from '@/components/FieldHelp';

export interface InlineEditOption {
  value: string;
  label: string;
}

export type InlineEditType = 'int' | 'text' | 'email' | 'bool' | 'select' | 'list';
export type InlineEditValue = string | boolean | string[];

/** Content longer than this (value or placeholder) edits in a Popover, not inline. */
const POPOVER_THRESHOLD = 28;

/** The chrome every field carries, independent of which control fills it. */
export interface FieldChrome {
  label: string;
  /** Short prose about the field — rendered in a popover, not under the control,
   *  so help-bearing actions don't double their row height. */
  help?: string;
  /** The action cannot run without a value here. Marked, never colour alone. */
  required?: boolean;
  /** True when a required field has no value yet — drives the warning treatment. */
  unfilled?: boolean;
  /** Why the value is still missing, from the API's `missing_inputs` entry. */
  missingWhy?: string;
  /** Validation message — client-side, or the per-key message `configure` returns. */
  error?: string;
  /** What the current value means for the operator when that deserves a warning
   *  (not an error: the value is valid, its consequence is the point). */
  warning?: string;
  /** The platform default, already formatted for display. */
  defaultLabel?: string;
  /** Revert this field to `defaultLabel`; omitted when that would be a no-op. */
  onRevert?: () => void;
  /** Controls rendered beside the label — the reveal/copy pair on a secret. */
  actions?: ReactNode;
}

interface Props extends FieldChrome {
  type: InlineEditType;
  value: InlineEditValue;
  /** value differs from the persisted one → render distinctly in read mode. */
  pending?: boolean;
  /** read-only (viewer) — shows the value but never enters edit mode. */
  disabled?: boolean;
  options?: InlineEditOption[];
  /** hint shown (muted) when the value is empty, e.g. the default or "Set…". */
  placeholder?: string;
  /** bool only: nothing is stored and `value` is the default that applies — said on the
   *  chip, the way a text field shows its default as a greyed placeholder. */
  unsetDefault?: boolean;
  /** Unit rendered after the value ("min before"), so it leaves the label. */
  suffix?: string;
  onChange: (v: InlineEditValue) => void;
}

function displayText(type: InlineEditType, value: InlineEditValue, options?: InlineEditOption[]): string {
  if (type === 'select') {
    const opt = (options ?? []).find((o) => o.value === String(value ?? ''));
    return opt ? opt.label : String(value ?? '');
  }
  return value != null ? String(value) : '';
}

export function InlineEditField({
  type,
  value,
  pending = false,
  disabled = false,
  options,
  placeholder,
  unsetDefault = false,
  suffix,
  onChange,
  ...chrome
}: Props) {
  const [editing, setEditing] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const snapshot = useRef<InlineEditValue>(value);
  const [addDraft, setAddDraft] = useState('');

  const startEditing = () => {
    if (disabled) return;
    snapshot.current = value;
    setEditing(true);
  };
  const openPopover = (el: HTMLElement) => {
    if (disabled) return;
    snapshot.current = value;
    setAnchorEl(el);
  };
  const commit = () => setEditing(false);
  /** A list's Done keeps what is typed in the add box: an address typed and confirmed
   *  with the check button (rather than Enter) used to be dropped without a word. */
  const commitList = (arr: string[]) => {
    const raw = addDraft.trim();
    if (raw) onChange([...arr, raw]);
    setAddDraft('');
    setEditing(false);
  };
  const closePopover = () => setAnchorEl(null);
  const revert = () => {
    onChange(snapshot.current);
    setEditing(false);
    setAnchorEl(null);
  };
  const invalid = Boolean(chrome.error);

  // ---- bool: a clickable chip, toggles in place (no edit mode) -------------
  if (type === 'bool') {
    const on = Boolean(value);
    const inherited = unsetDefault && !pending;
    return (
      <FieldBlock {...chrome}>
        <Tooltip
          title={pending ? 'Unsaved change' : inherited ? 'Not set — the default applies' : ''}
          disableInteractive
        >
          <Chip
            size="small"
            label={`${on ? 'Enabled' : 'Disabled'}${inherited ? ' (default)' : ''}`}
            color={on ? 'success' : 'default'}
            variant={pending || inherited ? 'outlined' : 'filled'}
            onClick={disabled ? undefined : () => onChange(!on)}
            sx={{
              fontStyle: pending ? 'italic' : 'normal',
              fontWeight: pending ? 600 : 400,
              cursor: disabled ? 'default' : 'pointer',
            }}
          />
        </Tooltip>
      </FieldBlock>
    );
  }

  // ---- list: chips (delete) + add box while editing -----------------------
  if (type === 'list') {
    const arr = Array.isArray(value) ? value : [];
    const addChip = () => {
      const raw = addDraft.trim();
      if (!raw) return;
      onChange([...arr, raw]);
      setAddDraft('');
    };
    return (
      <FieldBlock {...chrome}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          {arr.length === 0 && !editing && (
            <ClickableText
              pending={pending}
              empty
              invalid={invalid}
              unfilled={chrome.unfilled}
              onClick={startEditing}
              text={placeholder || 'Set…'}
            />
          )}
          {arr.map((tag, i) => (
            <Chip
              key={`${tag}-${i}`}
              size="small"
              label={tag}
              variant={pending ? 'outlined' : 'filled'}
              onClick={!editing ? startEditing : undefined}
              onDelete={editing ? () => onChange(arr.filter((_, j) => j !== i)) : undefined}
              sx={{ fontStyle: pending ? 'italic' : 'normal' }}
            />
          ))}
          {editing && (
            <>
              <TextField
                autoFocus
                variant="standard"
                placeholder="add…"
                value={addDraft}
                onChange={(e) => setAddDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addChip();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    revert();
                  }
                }}
                sx={{ width: 180, '& input': { fontSize: 12, py: 0.25 } }}
              />
              <IconButton size="small" aria-label="Done" onClick={() => commitList(arr)}>
                <CheckIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      </FieldBlock>
    );
  }

  // ---- select: read text ⇄ dropdown ---------------------------------------
  if (type === 'select') {
    if (editing) {
      return (
        <FieldBlock {...chrome}>
          <TextField
            select
            autoFocus
            variant="standard"
            value={String(value ?? '')}
            onChange={(e) => {
              onChange(e.target.value);
              setEditing(false);
            }}
            SelectProps={{ defaultOpen: true, onClose: () => setEditing(false) }}
            sx={{ minWidth: 140, '& .MuiInputBase-input': { fontSize: 14, py: 0.25 } }}
          >
            {(options ?? []).map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </FieldBlock>
      );
    }
    const text = displayText(type, value, options);
    return (
      <FieldBlock {...chrome}>
        <ClickableText
          pending={pending}
          empty={!text}
          invalid={invalid}
          unfilled={chrome.unfilled}
          onClick={startEditing}
          text={text || placeholder || 'Set…'}
          // A constrained field must look constrained — without this, a select
          // reads exactly like free text until the operator clicks it.
          adornment={<ArrowDropDownIcon sx={{ fontSize: 16, color: 'text.disabled', ml: 0.25 }} />}
        />
      </FieldBlock>
    );
  }

  // ---- int / text / email -------------------------------------------------
  const text = displayText(type, value);
  const longText =
    (type === 'text' || type === 'email') &&
    Math.max(text.length, (placeholder ?? '').length) > POPOVER_THRESHOLD;

  // Long text/email → edit in a Popover so width isn't clamped by the row and the
  // read text (the anchor) doesn't move.
  if (longText) {
    return (
      <FieldBlock {...chrome}>
        <ClickableText
          pending={pending}
          empty={!text}
          invalid={invalid}
          unfilled={chrome.unfilled}
          onClick={(e) => openPopover(e.currentTarget)}
          text={text || placeholder || 'Set…'}
        />
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={closePopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ p: 1.5, width: 'min(92vw, 520px)' }}>
            <TextField
              fullWidth
              autoFocus
              multiline
              maxRows={5}
              size="small"
              type={type === 'email' ? 'email' : 'text'}
              label={chrome.label}
              value={String(value ?? '')}
              placeholder={placeholder}
              error={invalid}
              helperText={chrome.error}
              // Soft-wrap the long value across rows for full visibility; keep it a
              // single logical line (strip any newline the user might type).
              onChange={(e) => onChange(e.target.value.replace(/[\r\n]+/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  revert();
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button size="small" onClick={closePopover}>
                Done
              </Button>
            </Box>
          </Box>
        </Popover>
      </FieldBlock>
    );
  }

  // Short int/text/email → inline standard-underline input, sized to content.
  if (editing) {
    const chars = Math.min(Math.max(String(value ?? '').length + 3, 8), 36);
    return (
      <FieldBlock {...chrome}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <TextField
            autoFocus
            variant="standard"
            type={type === 'int' ? 'number' : type === 'email' ? 'email' : 'text'}
            value={String(value ?? '')}
            placeholder={placeholder}
            error={invalid}
            inputProps={
              type === 'int'
                ? { inputMode: 'numeric', step: 1, 'aria-label': chrome.label }
                : { 'aria-label': chrome.label }
            }
            onChange={(e) => onChange(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                revert();
              }
            }}
            sx={{
              width: `${chars}ch`,
              '& input': {
                fontSize: 14,
                py: 0.25,
                ...(type === 'int' ? { fontVariantNumeric: 'tabular-nums' } : {}),
              },
            }}
          />
          {suffix && <Unit text={suffix} />}
        </Box>
      </FieldBlock>
    );
  }
  return (
    <FieldBlock {...chrome}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <ClickableText
          pending={pending}
          empty={!text}
          invalid={invalid}
          unfilled={chrome.unfilled}
          onClick={startEditing}
          text={text || placeholder || 'Set…'}
          tabular={type === 'int'}
        />
        {suffix && <Unit text={suffix} />}
      </Box>
    </FieldBlock>
  );
}

// --------------------------------------------------------------------------

function Unit({ text }: { text: string }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
      {text}
    </Typography>
  );
}

/** Label row + control + footer. Exported for callers supplying their own control. */
export function FieldBlock({
  label,
  help,
  required = false,
  unfilled = false,
  missingWhy,
  error,
  warning,
  defaultLabel,
  onRevert,
  actions,
  children,
}: FieldChrome & { children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.25, minHeight: 20 }}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
          {label}
          {required && (
            // Marker AND colour — the design system forbids signalling by colour
            // alone, and an unfilled required field is the one that needs finding.
            <Typography
              component="span"
              aria-label="required"
              sx={{ ml: 0.25, fontWeight: 700, color: unfilled ? 'warning.main' : 'text.disabled' }}
            >
              *
            </Typography>
          )}
        </Typography>
        {help && <FieldHelp help={{ title: label, body: help }} />}
        {actions}
      </Box>
      {children}
      {(error || missingWhy || warning || defaultLabel) && (
        <Box sx={{ mt: 0.25 }}>
          {error && (
            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
              {error}
            </Typography>
          )}
          {!error && missingWhy && (
            <Typography variant="caption" color="warning.dark" sx={{ display: 'block' }}>
              {missingWhy}
            </Typography>
          )}
          {warning && (
            <Typography
              variant="caption"
              color="warning.dark"
              role="note"
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}
            >
              <WarningAmberIcon sx={{ fontSize: 14, mt: '1px' }} aria-hidden />
              {warning}
            </Typography>
          )}
          {defaultLabel && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
              Default: {defaultLabel}
              {onRevert && (
                <>
                  {' · '}
                  <Link component="button" type="button" variant="caption" onClick={onRevert}>
                    Reset
                  </Link>
                </>
              )}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

/** Read-mode value rendered as compact clickable text (dashed underline hint). */
function ClickableText({
  text,
  pending,
  empty,
  invalid = false,
  unfilled = false,
  tabular = false,
  adornment,
  onClick,
}: {
  text: string;
  pending: boolean;
  empty: boolean;
  invalid?: boolean;
  unfilled?: boolean;
  tabular?: boolean;
  adornment?: ReactNode;
  onClick: (e: MouseEvent<HTMLElement>) => void;
}) {
  const borderColor = invalid
    ? 'error.main'
    : pending
      ? 'warning.main'
      : unfilled && empty
        ? 'warning.light'
        : 'divider';
  const textColor = invalid
    ? 'error.main'
    : empty
      ? 'text.disabled'
      : pending
        ? 'warning.dark'
        : 'text.primary';
  return (
    <Tooltip title={pending ? 'Unsaved change' : ''} disableInteractive>
      <ButtonBase
        onClick={onClick}
        sx={{
          justifyContent: 'flex-start',
          textAlign: 'left',
          px: 0.5,
          py: 0.25,
          maxWidth: '100%',
          borderRadius: 0.5,
          borderBottom: '1px dashed',
          borderColor,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontStyle: pending ? 'italic' : 'normal',
            fontWeight: pending ? 600 : 400,
            color: textColor,
            wordBreak: 'break-word',
            ...(tabular ? { fontVariantNumeric: 'tabular-nums' } : {}),
          }}
        >
          {text}
          {pending ? ' •' : ''}
        </Typography>
        {adornment}
      </ButtonBase>
    </Tooltip>
  );
}
